import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RestAtShoreButton } from '@/components/innerworld';
import { useInnerHome } from '@/hooks/useInnerWorld';

const ELEMENT_GLYPHS = {
  plants: 'M12 21v-8M12 13c0-4 3-6 7-6 0 4-3 6-7 6zM12 17c0-3-2.5-5-6-5 0 3 2.5 5 6 5z',
  front_door: 'M6 21V5a2 2 0 012-2h8a2 2 0 012 2v16M6 21h12M14 12h.5',
  library: 'M4 19V6h4v13M10 19V4h4v15M16 19V8h4v11M2 21h20',
  garden: 'M7 21c0-4 2-7 5-7s5 3 5 7M12 14V9M9 6a3 3 0 006 0 3 3 0 00-6 0z',
  memorial_room: 'M12 3l7 5v13H5V8zM10 21v-5h4v5M12 10v3',
  fireplace: 'M5 21V9h14v12M5 21h14M12 12c1.5 2 2.5 3.2 2.5 4.7a2.5 2.5 0 11-5 0c0-1.5 1-2.7 2.5-4.7z',
  lantern: 'M12 3v2M9 7h6l1 9a4 4 0 01-8 0zM12 10v3M10 21h4',
  reading_nook: 'M4 19V9a2 2 0 012-2h3v12M20 19V9a2 2 0 00-2-2h-3v12M4 19h16M9 11h6',
};

export default function InnerHomePage() {
  const navigate = useNavigate();
  const { home, loading } = useInnerHome();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FBF9F6' }}>
        <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: '#C9A84C', boxShadow: '0 0 20px rgba(201,168,76,0.6)' }} />
      </div>
    );
  }

  const restoredMap = Object.fromEntries((home?.restored_elements || []).map((e) => [e.element, e]));

  return (
    <div className="min-h-screen pb-16" style={{ background: '#FBF9F6' }} data-testid="inner-home-screen">
      <header className="max-w-md mx-auto px-6 pt-8 pb-2 flex items-center justify-between">
        <button onClick={() => navigate('/inner-world')} className="flex items-center gap-2 text-xs tracking-[0.1em]" style={{ fontFamily: 'Jost, sans-serif', color: '#6B6878' }} data-testid="home-back-button">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 18l-6-6 6-6" /></svg>
          THE MAP
        </button>
        <RestAtShoreButton />
      </header>

      <main className="max-w-md mx-auto px-6 pt-6">
        <div className="text-center mb-10">
          <span className="text-xs tracking-[0.2em] block mb-3" style={{ fontFamily: 'Jost, sans-serif', color: '#c0856b' }}>YOUR INNER HOME</span>
          <p className="text-base leading-relaxed" style={{ fontFamily: 'Playfair Display, serif', color: '#4A4A5A' }}>
            A house that fills in as your Council grows. Nothing here is ever taken away.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {(home?.all_elements || []).map(({ element, label }) => {
            const restored = restoredMap[element];
            return (
              <div
                key={element}
                className="p-5 rounded-2xl transition-all duration-500"
                style={{
                  background: restored ? 'rgba(192,133,107,0.1)' : '#FAF7F3',
                  border: restored ? '1px solid rgba(192,133,107,0.25)' : '1px solid transparent',
                  opacity: restored ? 1 : 0.45,
                }}
                data-testid={`home-element-${element}`}
              >
                <div
                  className="w-10 h-10 rounded-full mb-3 flex items-center justify-center"
                  style={{ background: restored ? 'rgba(192,133,107,0.18)' : '#F7F1EE' }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={restored ? '#c0856b' : '#B4B0BC'} strokeWidth="1.5" strokeLinejoin="round">
                    <path d={ELEMENT_GLYPHS[element]} />
                  </svg>
                </div>
                <p className="text-xs leading-relaxed mb-1" style={{ fontFamily: 'Jost, sans-serif', color: restored ? '#1A1A2E' : '#8A8798' }}>
                  {label}
                </p>
                {restored && (
                  <p className="text-[11px]" style={{ fontFamily: 'Jost, sans-serif', color: '#8A6A61' }}>
                    Restored with {restored.unlocked_via}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-center mt-10 leading-relaxed" style={{ fontFamily: 'Jost, sans-serif', color: '#8A8798' }}>
          Each part of you that joins the Council restores something here.
        </p>
      </main>
    </div>
  );
}
